import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "react-draggable";
import { useHistory } from "react-router-dom";

import actions from "../actions";
import { bulkResetBreadCrumpData, showAlert } from "../../../config/utils";
import { MASTER_FILTER_KEYS } from "../constants";
import { values } from "lodash";
import buildingActions from "../../building/actions";
import recommendationActions from "../../recommendations/actions";
import { DYNAMIC_FIELD_TYPES } from "../../common/constants";
import Portal from "../../common/components/Portal";
import HeaderConfigModal from "./HeaderConfigModal/HeaderConfigModal";

const CreateSkeleton = ({ onCancel, templateId, closeViewPropertyModal, selectedClient, templateName = "", chartData }) => {
    const coreReportCreate = useRef(null);
    const [showErrorBorder, setShowErrorBorder] = useState(false);
    const [skeletonData, setSkeletonData] = useState({ name: templateName, notes: "" });
    const [globalChecked, setGlobalChecked] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();

    const [filterLists, setFilterLists] = useState({});
    const [dummyFilterLists, setDummyFilterList] = useState({});
    const [showHeadingConfig, setShowHeadingConfig] = useState(false);
    const [headingConfigData, setHeadingConfigData] = useState([]);
    const { building_custom_field } = useSelector(state => state.buildingReducer?.buildingDynamicFields || {});
    const { recommendation_custom_fields } = useSelector(state => state.recommendationsReducer?.dynamicFields || {});
    const { filterFields } = useSelector(state => state?.dashboardReducer);
    const { coreReportCreateDataResponse } = useSelector(state => state.smartChartReducer) || {};
    useEffect(() => {
        if (coreReportCreate?.current && coreReportCreateDataResponse) {
            const permissionError = coreReportCreateDataResponse?.detail;
            if (coreReportCreateDataResponse?.success) {
                showAlert(coreReportCreateDataResponse?.message ?? "Created Successfully");
                bulkResetBreadCrumpData([
                    { key: "main", name: "Smart Reports & Charts", path: `/smartcharts/reports` },
                    {
                        key: "corereports",
                        name: "Core Reports",
                        path: `/smartcharts/corereports`
                    }
                ]);
                closeViewPropertyModal();
                history.push(`/smartcharts/corereports`);
            } else {
                showAlert(typeof permissionError === "string" ? permissionError : coreReportCreateDataResponse?.error);
            }
            coreReportCreate.current = false;
        }
    }, [coreReportCreateDataResponse]);

    useEffect(() => {
        const CustomFieldChange = (customFields, prefix) => {
            const filters = {};
            customFields &&
                Object.entries(customFields || {}).forEach(([_, item]) => {
                    item.filter(field =>
                        [
                            DYNAMIC_FIELD_TYPES.SELECT,
                            DYNAMIC_FIELD_TYPES.BOOLEAN,
                            DYNAMIC_FIELD_TYPES.NUMBER,
                            DYNAMIC_FIELD_TYPES.CURRENCY,
                            DYNAMIC_FIELD_TYPES?.TEXT
                        ].includes(field.field_type)
                    ).forEach(field => {
                        const paramKey =
                            field.field_type === DYNAMIC_FIELD_TYPES.SELECT || field.field_type === DYNAMIC_FIELD_TYPES.BOOLEAN
                                ? `${prefix}__${field?.field_name}`
                                : `${prefix}s.${field?.field_name}`;
                        if (
                            ((chartData?.["project"]?.band1.mfilter && Object.keys(chartData?.["project"]?.band1.mfilter || {}).includes(paramKey)) ||
                                (chartData?.["project"]?.band1?.mfilter?.filters &&
                                    Object.keys(chartData["project"]?.band1?.mfilter?.filters || {}).includes(paramKey))) &&
                            paramKey !== "client_ids"
                        ) {
                            filters[paramKey] = {
                                required: true,
                                keep_values: false
                            };
                        }
                    });
                });
            return filters;
        };

        const dynamicMasterFilters = building_custom_field ? CustomFieldChange(building_custom_field, "building_dynamic_field") : {};
        const projectFilters = recommendation_custom_fields ? CustomFieldChange(recommendation_custom_fields, "recommendation_dynamic_field") : {};
        setFilterLists(prevState => ({
            ...prevState,
            project: {
                ...prevState.project,
                ...dynamicMasterFilters,
                ...projectFilters
            }
        }));
        setDummyFilterList(prevState => ({
            ...prevState,
            project: {
                ...prevState.project,
                ...dynamicMasterFilters,
                ...projectFilters
            }
        }));
    }, [building_custom_field, recommendation_custom_fields]);

    useEffect(() => {
        const clientId = chartData["project"]?.band1?.mfilter?.client_ids;
        const project_id = chartData["project"]?.band1?.mfilter?.project_ids;
        if (clientId) dispatch(buildingActions.getBuildingDynamicFields({ client_id: clientId }));
        project_id && dispatch(recommendationActions.getDynamicFields({ project_id }));

        const filteredDatas = {};
        const filterValue = {};
        if (filterFields && chartData["project"]?.band1?.mfilter) {
            filterFields.forEach(filter => {
                const paramKey = filter.paramKey;
                const currectValue = chartData["project"]?.band1?.mfilter;
                const filterKeyValue =
                    ((currectValue?.filters?.[paramKey]?.key !== "" && currectValue?.filters?.[paramKey]?.key !== null) ||
                        ((currectValue?.filters?.[paramKey]?.filters[0] === "null" ||
                            currectValue?.filters?.[paramKey]?.filters[0] === "not_null" ||
                            currectValue?.filters?.[paramKey]?.filters[0] === "count_gt_1") &&
                            (currectValue?.filters?.[paramKey]?.key === "" || currectValue?.filters?.[paramKey]?.key === null))) &&
                    currectValue?.filters?.[paramKey]?.filters;
                if (
                    (Object.keys(chartData["project"]?.band1?.mfilter || {})?.includes(paramKey) ||
                        filterKeyValue ||
                        currectValue?.top_n?.[paramKey] ||
                        ((chartData["project"]?.band1?.mfilter.start_year || chartData["project"]?.band1?.mfilter?.end_year) &&
                            paramKey === "year")) &&
                    paramKey !== "client_ids"
                ) {
                    if (paramKey === "project_ids") {
                        filterValue[paramKey] = {
                            required: true,
                            keep_values: true
                        };
                    } else if (
                        Object.keys(chartData?.["project"]?.band1?.mfilter || {})?.includes(paramKey) ||
                        filterKeyValue ||
                        currectValue?.top_n?.[paramKey] ||
                        ((chartData["project"]?.band1?.mfilter.start_year || chartData["project"]?.band1?.mfilter?.end_year) && paramKey === "year")
                    ) {
                        filterValue[paramKey] = {
                            required: true,
                            keep_values: false
                        };
                    }
                }
            });
            filteredDatas["project"] = filterValue;
        }
        setFilterLists(filteredDatas);
        setDummyFilterList(filteredDatas);
    }, [chartData]);
    const validate = () => {
        setShowErrorBorder(false);
        if (!skeletonData.name && !skeletonData.name.trim().length) {
            setShowErrorBorder(true);
            return false;
        }
        return true;
    };

    const createNewSkeleton = () => {
        if (validate()) {
            const { name, notes } = skeletonData;
            const payload = {
                name,
                notes,
                property_id: templateId,
                core_report_req_filters: filterLists,
                custom_header_keys: headingConfigData
            };

            if (!globalChecked) {
                payload.client_id = selectedClient;
            }
            coreReportCreate.current = true;
            dispatch(actions.createSkeletonFromTemplate(payload));
        }
    };
    const processCustomFields = (item, prefix, filteredData) => {
        const fields = item.filter(field =>
            [
                DYNAMIC_FIELD_TYPES.SELECT,
                DYNAMIC_FIELD_TYPES.BOOLEAN,
                DYNAMIC_FIELD_TYPES.NUMBER,
                DYNAMIC_FIELD_TYPES.CURRENCY,
                DYNAMIC_FIELD_TYPES?.TEXT
            ].includes(field.field_type)
        );
        fields.forEach(field => {
            const paramKey =
                field.field_type === DYNAMIC_FIELD_TYPES.SELECT || field.field_type === DYNAMIC_FIELD_TYPES.BOOLEAN
                    ? `${prefix}__${field.field_name}`
                    : `${prefix}s.${field.field_name}`;

            if (
                chartData["project"]?.band1?.mfilter?.[paramKey]?.length > 0 ||
                (chartData["project"].band1.mfilter?.filters && Object.keys(chartData["project"].band1.mfilter?.filters || {}).includes(paramKey))
            ) {
                filteredData[paramKey] = {
                    label: field.label,
                    key: field.field_name,
                    paramKey: paramKey
                };
            }
        });
    };
    const getSelectedFilters = () => {
        if (globalChecked) {
            const filteredDatas = {};
            const GeoHierarchyFields = ["Projects", "Region", "Site", "Building Types", "Buildings", "Years", "start", "end", "Floor", "Addition"];
            const entityFilters = MASTER_FILTER_KEYS["project"]?.filter(e => GeoHierarchyFields.includes(e.label));
            if (entityFilters && chartData["project"]?.band1?.mfilter) {
                const filteredData = {};
                entityFilters.forEach(filter => {
                    const paramKey = filter.paramKey;
                    if (Object.keys(chartData["project"].band1.mfilter || {}).includes(paramKey)) {
                        if (chartData["project"]?.band1?.mfilter?.[paramKey]?.length > 0) {
                            filteredData[paramKey] = filter;
                        }
                    }
                });
                filteredDatas["project"] = filteredData;
            }
            return filteredDatas;
        } else {
            const filteredDatas = {};
            const entityFilters = filterFields
                ?.filter(e => e.label !== "Clients")
                ?.sort((a, b) => {
                    if (a.label === "FCA Projects") return -1;
                    if (b.label === "FCA Projects") return 1;
                    return 0;
                });
            if (entityFilters && chartData["project"]?.band1?.mfilter) {
                const filteredData = {};
                entityFilters.forEach(filter => {
                    const paramKey = filter.paramKey;
                    if (
                        Object.keys(chartData["project"].band1.mfilter || {})?.includes(paramKey) ||
                        Object.keys(chartData["project"].band1.mfilter?.filters || {})?.includes(paramKey) ||
                        chartData["project"].band1.mfilter?.top_n?.[paramKey] ||
                        ((chartData["project"]?.band1?.mfilter.start_year || chartData["project"]?.band1?.mfilter?.end_year) && paramKey === "year")
                    ) {
                        const currectValue = chartData["project"]?.band1?.mfilter;
                        if (
                            chartData["project"]?.band1?.mfilter?.[paramKey]?.length > 0 ||
                            (((currectValue?.filters?.[paramKey]?.key !== "" && currectValue?.filters?.[paramKey]?.key !== null) ||
                                ((currectValue?.filters?.[paramKey]?.filters[0] === "null" ||
                                    currectValue?.filters?.[paramKey]?.filters[0] === "not_null" ||
                                    currectValue?.filters?.[paramKey]?.filters[0] === "count_gt_1") &&
                                    (currectValue?.filters?.[paramKey]?.key === "" || currectValue?.filters?.[paramKey]?.key === null))) &&
                                currectValue?.filters?.[paramKey]?.filters) ||
                            currectValue?.top_n?.[paramKey] ||
                            ((chartData["project"]?.band1?.mfilter.start_year || chartData["project"]?.band1?.mfilter?.end_year) &&
                                paramKey === "year")
                        ) {
                            filteredData[paramKey] = filter;
                        }
                    }
                });
                building_custom_field &&
                    Object.entries(building_custom_field || {})?.forEach(([_, item]) => {
                        processCustomFields(item, "building_dynamic_field", filteredData);
                    });

                recommendation_custom_fields &&
                    Object.entries(recommendation_custom_fields || {})?.forEach(([_, item]) => {
                        processCustomFields(item, "recommendation_dynamic_field", filteredData);
                    });
                filteredDatas["project"] = filteredData;
            }

            return filteredDatas;
        }
    };

    const handleFilterClick = (entityName, label, keyLabel, checked) => {
        setFilterLists(prevState => {
            const newState = { ...prevState };
            if (newState[entityName] && newState[entityName][label]) {
                newState[entityName][label][keyLabel] = checked;
                if (checked && keyLabel === "keep_values") {
                    newState[entityName][label].required = true;
                }
            }
            return newState;
        });
    };

    const renderHeadingConfigModal = () => {
        if (!showHeadingConfig) return null;
        return (
            <Portal
                body={
                    <HeaderConfigModal
                        mfilter={filterLists}
                        headerConfigData={headingConfigData}
                        onClose={() => setShowHeadingConfig(false)}
                        isCoreReportCreate={true}
                        onSubmit={(header_data = []) => {
                            setHeadingConfigData(header_data);
                            setShowHeadingConfig(false);
                        }}
                    />
                }
                onCancel={() => {}}
            />
        );
    };

    return (
        <>
            <div
                className="modal modal-region modal-view inbox-modal smart-chart-popup smart-dtl-pop core-rpt-mode"
                id="modalId"
                tabIndex="-1"
                style={{ display: "block" }}
            >
                <Draggable handle=".draggable" cancel=".non-draggable">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content region-mng">
                            <div className="modal-header draggable">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    <div className="txt-hed">Create Core Report</div>
                                </h5>
                                <button type="button" className="close non-draggable" onClick={onCancel}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className={`modal-body region-otr sm-chart-upload`}>
                                <div className="col-md-12 main-sec">
                                    <div className="form-row">
                                        <div className="form-group col-12 mb-3">
                                            <label>Name</label>
                                            <input
                                                type="text"
                                                placeholder="Name"
                                                value={skeletonData.name}
                                                onChange={e => {
                                                    let value = e.target.value;
                                                    setSkeletonData(prevParams => {
                                                        return {
                                                            ...prevParams,
                                                            name: value
                                                        };
                                                    });
                                                }}
                                                className={`form-control ${
                                                    showErrorBorder && !skeletonData.name && !skeletonData.name.trim().length ? "error-border" : ""
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group col-12 mb-4">
                                            <label>Notes</label>
                                            <textarea
                                                className="form-control textarea"
                                                value={skeletonData.notes}
                                                onChange={e => {
                                                    let value = e.target.value;
                                                    setSkeletonData(prevParams => {
                                                        return {
                                                            ...prevParams,
                                                            notes: value
                                                        };
                                                    });
                                                }}
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div class="list-badge-outer ">
                                        <div className="list-inner col-md-12 d-flex justify-content-end">
                                            <button
                                                className="filter-button blue-common-btn heading-blue-button"
                                                data-tip="View Filters"
                                                data-for="dashboard-icons-right"
                                                currentitem="false"
                                                onClick={() => {
                                                    setShowHeadingConfig(true);
                                                }}
                                            >
                                                <span className="pr-2 pl-2">Page Header Config</span>
                                                {(headingConfigData || [])?.length > 0 && (
                                                    <span className="notifyTxt">{(headingConfigData || [])?.length}</span>
                                                )}
                                            </button>
                                        </div>
                                        {Object.entries(getSelectedFilters())?.map(([entityName, filterLabels], index) =>
                                            Object.entries(filterLabels).map(([key, Values], index) => (
                                                <div class="list-inner col-md-6" key={index}>
                                                    <div className="d-flex align-items-center">
                                                        <h3 className="mb-0">{Values?.label === "FCA Projects" ? "Projects" : Values?.label}</h3>
                                                        {!globalChecked && (
                                                            <div className="formInp">
                                                                <div className="col-md-12 ">
                                                                    <span className="dropdown-item">
                                                                        <label className="container-check mb-0">
                                                                            Keep values
                                                                            <input
                                                                                type="checkbox"
                                                                                onClick={e => {
                                                                                    handleFilterClick(
                                                                                        entityName,
                                                                                        key,
                                                                                        "keep_values",
                                                                                        e.target.checked
                                                                                    );
                                                                                }}
                                                                                checked={filterLists?.[entityName]?.[key]?.keep_values}
                                                                                disabled={Values?.label === "FCA Projects"}
                                                                            />
                                                                            <span
                                                                                className={`checkmark ${
                                                                                    Values?.label === "FCA Projects" ? "cursor-notallowed" : ""
                                                                                }`}
                                                                            ></span>
                                                                        </label>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="formInp">
                                                            <div className="col-md-12 ">
                                                                <span className="dropdown-item">
                                                                    <label className="container-check mb-0">
                                                                        Required
                                                                        <input
                                                                            type="checkbox"
                                                                            onClick={e => {
                                                                                handleFilterClick(entityName, key, "required", e.target.checked);
                                                                            }}
                                                                            checked={
                                                                                filterLists?.[entityName]?.[key]
                                                                                    ? filterLists?.[entityName]?.[key]?.required
                                                                                    : true
                                                                            }
                                                                            disabled={
                                                                                Values?.label === "FCA Projects" ||
                                                                                filterLists?.[entityName]?.[key]?.keep_values
                                                                            }
                                                                        />
                                                                        <span
                                                                            className={`checkmark ${
                                                                                Values?.label === "FCA Projects" ||
                                                                                filterLists?.[entityName]?.[key]?.keep_values
                                                                                    ? "cursor-notallowed"
                                                                                    : ""
                                                                            }`}
                                                                        ></span>
                                                                    </label>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ul class="badge-ul d-flex">
                                                        {chartData?.[entityName]?.["band1"]?.filter_label !== 0 &&
                                                            (Values?.label === "Years" ? (
                                                                <>
                                                                    {["Start Year", "End Year"].map(yearLabel => (
                                                                        <li class="badge-list" key={yearLabel}>
                                                                            <span>
                                                                                {yearLabel}:{" "}
                                                                                {chartData?.[entityName]?.["band1"]?.filter_label?.[yearLabel] || ""}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                chartData?.[entityName]?.["band1"]?.filter_label?.[
                                                                    Values?.label === "FCA Projects"
                                                                        ? "Projects"
                                                                        : (Values?.label === "Region" && globalChecked) ||
                                                                          (Values?.label === "Site" && globalChecked)
                                                                        ? `${Values?.label}s`
                                                                        : Values?.label
                                                                ]?.map((filterValue, i) => (
                                                                    <li class="badge-list" key={i}>
                                                                        <span>
                                                                            {typeof filterValue === "object"
                                                                                ? filterValue && Object.values(filterValue).join(", ")
                                                                                : filterValue}
                                                                        </span>
                                                                    </li>
                                                                ))
                                                            ))}
                                                    </ul>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="formInp checkbox-outr-wrap mt-3">
                                        <div className="col-md-12 checkbox-cover">
                                            <span className="dropdown-item">
                                                <label className="container-check">
                                                    Global Core Report
                                                    <input
                                                        type="checkbox"
                                                        onClick={() => {
                                                            setGlobalChecked(prevGlobalChecked => !prevGlobalChecked);
                                                            if (!globalChecked) {
                                                                const GeoHierarchyFields = [
                                                                    "Projects",
                                                                    "Region",
                                                                    "Site",
                                                                    "Building Types",
                                                                    "Buildings",
                                                                    "Years",
                                                                    "start",
                                                                    "end",
                                                                    "Floor",
                                                                    "Addition"
                                                                ];
                                                                const entityFilters = MASTER_FILTER_KEYS["project"]?.filter(e =>
                                                                    GeoHierarchyFields.includes(e.label)
                                                                );
                                                                setFilterLists(prevState => {
                                                                    const newState = { ...prevState };
                                                                    Object.keys(newState)?.forEach(entityName => {
                                                                        Object.keys(newState[entityName])?.forEach(label => {
                                                                            Object.keys(newState[entityName][label])?.forEach(keyLabel => {
                                                                                if (keyLabel === "keep_values") {
                                                                                    newState[entityName][label][keyLabel] = false;
                                                                                }
                                                                            });
                                                                        });
                                                                    });
                                                                    const paramKeys = entityFilters?.map(item => item?.paramKey);

                                                                    const filteredData = {
                                                                        project: Object.keys(newState?.project || {})
                                                                            .filter(key => paramKeys?.includes(key))
                                                                            .reduce((obj, key) => {
                                                                                obj[key] = newState?.project[key];
                                                                                return obj;
                                                                            }, {})
                                                                    };
                                                                    return filteredData;
                                                                });
                                                            } else {
                                                                setFilterLists(prevState => {
                                                                    const newState = { ...dummyFilterLists };
                                                                    Object.keys(newState)?.forEach(entityName => {
                                                                        Object.keys(newState[entityName])?.forEach(label => {
                                                                            if (label === "project_ids") {
                                                                                Object.keys(newState[entityName][label])?.forEach(keyLabel => {
                                                                                    if (keyLabel === "keep_values") {
                                                                                        newState[entityName][label][keyLabel] = true;
                                                                                    }
                                                                                });
                                                                            }
                                                                        });
                                                                    });
                                                                    return newState;
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="btn-sec">
                                    <div className="text-right btnOtr edit-cmn-btn mt-0">
                                        <button
                                            type="button"
                                            className="btn btn-primary btnRgion "
                                            class="btn btn-create save"
                                            onClick={() => createNewSkeleton()}
                                        >
                                            Create
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btnClr mr-1"
                                            data-dismiss="modal"
                                            onClick={() => onCancel()}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
            {renderHeadingConfigModal()}
        </>
    );
};

export default CreateSkeleton;
